/**
 * Layout primitives for the Fitness Village booking flow — cart, add-ons, checkout and
 * confirmation. The village pages set the rules these follow: 16px gutters and a 36px hero on
 * mobile, 40px gutters and a bigger hero from 960px, soft 20px cards with an eyebrow header, and
 * a 44px minimum target on every control.
 *
 * The flow is a single reading column (720px max), so a card never stretches past a comfortable
 * line length on desktop.
 */
import styled from '@emotion/styled';
import { Box, ButtonBase } from '@mui/material';
import { DFC_PALETTE, DFC_NEUTRAL, withAlpha } from '../../../../Utils/dfcBrand';
import { DFC_VILLAGE_DESKTOP_MEDIA } from '../../../../hooks/useDfcVillageDesktop';
import { SurfaceProp, focusRing, omit } from '../style';

export const FlowScreen = styled(Box, omit('surface'))<SurfaceProp>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-width: 0;
  min-height: calc(100vh - 60px);
  background: ${({ surface }) => surface.pageBg};
  color: ${({ surface }) => surface.ink};
  font-family: Inter, system-ui, sans-serif;
`;

export const FlowBody = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  max-width: 800px;
  min-width: 0;
  box-sizing: border-box;
  padding: 22px 16px 32px;

  ${DFC_VILLAGE_DESKTOP_MEDIA} {
    gap: 24px;
    padding: 34px 40px 48px;
  }
`;

/* ---- Hero ---- */

export const HeroBlock = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`;

export const BackPill = styled(ButtonBase, omit('surface'))<SurfaceProp>`
  && {
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-height: 44px;
    padding: 0 14px;
    border-radius: 999px;
    font-family: inherit;
    font-size: 12px;
    font-weight: 700;
    color: ${({ surface }) => surface.ink};
    border: 1px solid ${({ surface }) => surface.border};
    ${({ surface }) => focusRing(surface)}

    svg {
      width: 16px;
      height: 16px;
    }

    /* The chevron points back — towards the start edge, which is the right in Arabic. */
    [dir='rtl'] & svg {
      transform: scaleX(-1);
    }
  }
`;

/** The italic uppercase headline. Mobile 36px, the village page's size; 56px from 960px. */
export const Hero = styled('h1', omit('surface'))<SurfaceProp>`
  && {
    margin: 0;
    font-size: 36px;
    line-height: 0.92;
    /* Arabic letters need room above and below — display lines would touch at 0.9 */
    &:lang(ar) {
      line-height: 1.3;
    }
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: -0.02em;
    overflow-wrap: anywhere;
    color: ${({ surface }) => surface.ink};

    ${DFC_VILLAGE_DESKTOP_MEDIA} {
      font-size: 56px;
      line-height: 0.95;
      letter-spacing: -0.03em;
    }
  }
`;

/** Ink on light (DFC blue fails AA on white); green on dark — the village hero's correction. */
export const HeroAccent = styled('span', omit('surface'))<SurfaceProp>`
  font-weight: 800;
  font-style: italic;
  color: ${({ surface }) => (surface.dark ? DFC_PALETTE.green : surface.ink)};
`;

export const Lead = styled('p', omit('surface'))<SurfaceProp>`
  margin: 0;
  max-width: 560px;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ surface }) => surface.ink2};
  overflow-wrap: anywhere;

  ${DFC_VILLAGE_DESKTOP_MEDIA} {
    font-size: 15px;
  }
`;

/* ---- Section card ---- */

export const SectionCard = styled('section', omit('surface'))<SurfaceProp>`
  display: flex;
  flex-direction: column;
  min-width: 0;
  border-radius: 20px;
  background: ${({ surface }) => surface.surface};
  border: 1px solid ${({ surface }) => surface.border};
  overflow: hidden;
`;

/** Eyebrow on the left, a quiet detail on the right, a hairline beneath. */
export const SectionHead = styled(Box, omit('surface'))<SurfaceProp>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 48px;
  padding: 0 18px;
  border-bottom: 1px solid ${({ surface }) => surface.border};

  ${DFC_VILLAGE_DESKTOP_MEDIA} {
    padding: 0 22px;
  }
`;

/**
 * The section label — SectionLabel's eyebrow type, as a real heading. Secondary ink, not the
 * muted grey the mock uses (2.3:1 on white): a heading has to pass AA.
 */
export const SectionTitle = styled('h2', omit('surface'))<SurfaceProp>`
  margin: 0;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ surface }) => surface.ink2};
`;

export const SectionMeta = styled('span', omit('surface'))<SurfaceProp>`
  font-size: 12px;
  color: ${({ surface }) => surface.ink2};
  white-space: nowrap;
`;

export const SectionBody = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
  padding: 18px;

  ${DFC_VILLAGE_DESKTOP_MEDIA} {
    padding: 22px;
  }
`;

/* ---- Form fields ---- */

export const FieldGrid = styled(Box)`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 14px;

  @media (min-width: 560px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const FieldWrap = styled(Box)`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

export const FieldLabel = styled('label', omit('surface'))<SurfaceProp>`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${({ surface }) => surface.ink2};
`;

const fieldBase = (surface: SurfaceProp['surface'], hasError?: boolean) => `
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  height: 52px;
  border-radius: 12px;
  font-family: inherit;
  font-size: 15px;
  color: ${surface.ink};
  background: ${surface.soft};
  border: 1px solid ${hasError ? DFC_NEUTRAL.error : surface.border};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &::placeholder {
    color: ${surface.ink3};
    opacity: 1;
  }

  &:focus {
    outline: none;
    border-color: ${surface.dark ? DFC_PALETTE.green : surface.ink};
    box-shadow: 0 0 0 3px ${withAlpha(surface.dark ? DFC_PALETTE.green : surface.ink, 0.14)};
  }
`;

export const Field = styled('input', omit('surface', 'hasError'))<
  SurfaceProp & { hasError?: boolean }
>`
  ${({ surface, hasError }) => fieldBase(surface, hasError)}
  padding: 0 16px;
`;

/** Country code + number on one line; the select is sized to its "AE +971" content. */
export const PhoneRow = styled(Box)`
  display: flex;
  gap: 8px;
  min-width: 0;
`;

/**
 * Shows the compact "AE +971"; a transparent native select covers it, so picking a country is
 * the platform's own list (and keyboard) while the field stays narrow.
 */
export const CountryBox = styled(Box, omit('surface'))<SurfaceProp>`
  position: relative;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  height: 52px;
  padding: 0 28px 0 14px;
  border-radius: 12px;
  box-sizing: border-box;
  font-size: 15px;
  font-weight: 600;
  white-space: nowrap;
  color: ${({ surface }) => surface.ink};
  background: ${({ surface }) => surface.soft};
  border: 1px solid ${({ surface }) => surface.border};

  &::after {
    content: '';
    position: absolute;
    right: 12px;
    top: 50%;
    width: 6px;
    height: 6px;
    margin-top: -5px;
    /* The rotated right + bottom borders draw a down chevron; mirrored they would point sideways. */
    /* @noflip */
    border-right: 2px solid ${({ surface }) => surface.ink2};
    border-bottom: 2px solid ${({ surface }) => surface.ink2};
    transform: rotate(45deg);
  }

  &:focus-within {
    border-color: ${({ surface }) => (surface.dark ? DFC_PALETTE.green : surface.ink)};
    box-shadow: 0 0 0 3px
      ${({ surface }) => withAlpha(surface.dark ? DFC_PALETTE.green : surface.ink, 0.14)};
  }
`;

export const CountryNativeSelect = styled('select')`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  font-size: 16px;
`;

/* ---- Rows ---- */

/** A full-width check or radio row: the whole row is the 44px+ target. */
export const ChoiceRow = styled('label', omit('surface', 'checked'))<
  SurfaceProp & { checked: boolean }
>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-height: 44px;
  padding: 12px 14px;
  border-radius: 14px;
  cursor: pointer;
  background: ${({ surface, checked }) =>
    checked ? withAlpha(DFC_PALETTE.green, surface.dark ? 0.12 : 0.1) : surface.soft};
  border: 1px solid
    ${({ surface, checked }) =>
      checked ? (surface.dark ? DFC_PALETTE.green : surface.ink) : surface.border};
  font-size: 13px;
  line-height: 1.5;
  color: ${({ surface }) => surface.ink};

  &:focus-within {
    outline: 2px solid ${({ surface }) => (surface.dark ? DFC_PALETTE.green : DFC_PALETTE.black)};
    outline-offset: 2px;
  }

  input {
    flex: 0 0 auto;
    width: 20px;
    height: 20px;
    margin: 1px 0 0;
    accent-color: ${({ surface }) => (surface.dark ? DFC_PALETTE.green : DFC_PALETTE.black)};
    cursor: pointer;
  }
`;

export const InlineLink = styled(ButtonBase, omit('surface'))<SurfaceProp>`
  && {
    display: inline;
    vertical-align: baseline;
    font: inherit;
    font-weight: 700;
    color: ${({ surface }) => (surface.dark ? DFC_PALETTE.green : surface.ink)};
    text-decoration: underline;
    text-underline-offset: 3px;
    ${({ surface }) => focusRing(surface)}
  }
`;

export const ErrorText = styled('p')`
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: ${DFC_NEUTRAL.error};
`;

/** The full-width confirm action at the foot of the flow. */
export const PrimaryAction = styled(ButtonBase, omit('surface'))<SurfaceProp>`
  && {
    width: 100%;
    min-height: 52px;
    padding: 0 24px;
    border-radius: 999px;
    font-family: inherit;
    font-size: 15px;
    font-weight: 800;
    background: ${({ surface }) => surface.ctaBg};
    color: ${({ surface }) => surface.ctaFg};
    ${({ surface }) => focusRing(surface)}

    &.Mui-disabled {
      opacity: 0.4;
    }
  }
`;

/** A page-level action outside any card: full width on a phone, sized to its label on desktop. */
export const PageAction = styled(PrimaryAction)`
  ${DFC_VILLAGE_DESKTOP_MEDIA} {
    && {
      align-self: flex-start;
      width: auto;
      min-width: 280px;
    }
  }
`;
